import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useCurrency } from "../../context/CurrencyContext";
import rxLogo from "../../assets/rx-logo.png";
import { toast } from "sonner";
import { Globe, DollarSign, Facebook, Instagram, Twitter, Linkedin, Mail } from "lucide-react";

export default function Footer() {
  const { settings } = useCurrency();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      if (data?.already_subscribed) {
        toast.info("You're already on the list — thanks!");
      } else {
        toast.success("Subscribed! Watch your inbox for deals.");
      }
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0A1E1C] text-white/85 mt-24 grain-overlay" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        {/* Brand + Newsletter */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 pb-12 border-b border-white/10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                <img src={rxLogo} alt="RydeXpress" className="w-full h-full object-cover" />
              </div>
              <div className="font-display text-2xl font-bold text-white tracking-tight">RydeXpress</div>
            </div>
            <p className="mt-5 text-white/70 leading-relaxed max-w-md">
              Get deals and updates delivered to your inbox. Join our newsletter for the latest offers in Rwanda.
            </p>
            <form
              onSubmit={subscribe}
              className="mt-6 flex gap-3 max-w-md"
              data-testid="newsletter-form"
            >
              <div className="flex-1 relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
                <input
                  type="email"
                  required
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#0F2624] border border-white/10 rounded-full pl-11 pr-4 h-12 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#2A9D8F] transition-colors"
                  data-testid="newsletter-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#2A9D8F] hover:bg-[#23887A] disabled:opacity-60 text-white rounded-full px-6 h-12 font-semibold transition-colors"
                data-testid="newsletter-submit"
              >
                {submitting ? "…" : "Subscribe"}
              </button>
            </form>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
            <div>
              <h4 className="font-display font-bold text-white mb-3">Company</h4>
              <ul className="space-y-2.5 text-white/70">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold text-white mb-3">Support</h4>
              <ul className="space-y-2.5 text-white/70">
                <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us or Report an Issue</Link></li>
                <li><Link to="/safety" className="hover:text-white transition-colors">Safety</Link></li>
                <li><Link to="/legal/terms" className="hover:text-white transition-colors">Terms</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold text-white mb-3">Hosts</h4>
              <ul className="space-y-2.5 text-white/70">
                <li><Link to="/list-your-car" className="hover:text-white transition-colors">Become a Host</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="flex items-center gap-6 text-xs text-white/60">
            <span className="flex items-center gap-1.5"><Globe size={13} /> English</span>
            <span className="flex items-center gap-1.5"><DollarSign size={13} /> USD</span>
            <span className="hidden sm:inline">{settings?.platform_contact?.email || "info@rydexpress.com"} · {settings?.platform_contact?.phone || "+250 796 680 663"}</span>
          </div>
          <div className="flex items-center gap-4 text-white/60">
            <a href="https://www.facebook.com/share/18o1M6D3NU/?mibextid=wwXIfr" target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-white transition-colors"><Facebook size={16} /></a>
            <a href="https://www.instagram.com/ryde_xpress?utm_source=qr" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-white transition-colors"><Instagram size={16} /></a>
            <a href="https://x.com/ryde_xpress?s=11" target="_blank" rel="noreferrer" aria-label="X (Twitter)" className="hover:text-white transition-colors"><Twitter size={16} /></a>
            <a href="https://www.tiktok.com/@rydexpress?_r=1&_t=ZP-98FK7ZTSdTi" target="_blank" rel="noreferrer" aria-label="TikTok" className="hover:text-white transition-colors text-xs font-bold">TikTok</a>
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-6 text-xs text-white/40 justify-center sm:justify-start">
          <span>© {new Date().getFullYear()} RydeXpress.</span>
          <Link to="/legal/privacy" className="hover:text-white/70">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-white/70">Terms</Link>
        </div>
      </div>
    </footer>
  );
}
